import React, { useEffect, useState } from "react";

import {
  Box,
  Typography,
  CircularProgress,
  Backdrop,
  Paper,
  Stack,
  Fade
} from "@mui/material";
import { keyframes } from "@emotion/react";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import AutoGraphIcon from "@mui/icons-material/AutoGraph";

/* ===================== Animations ===================== */

const shimmerLTR = keyframes`
  0% { background-position: -150% 0; }
  100% { background-position: 150% 0; }
`;

const glowPulse = keyframes`
  0% { box-shadow: 0 0 8px rgba(33,150,243,0.4); }
  50% { box-shadow: 0 0 20px rgba(33,150,243,0.75); }
  100% { box-shadow: 0 0 8px rgba(33,150,243,0.4); }
`;

const bounce = keyframes`
  0% { transform: scale(0.9); opacity: 0; }
  60% { transform: scale(1.1); opacity: 1; }
  100% { transform: scale(1); }
`;

/* ===================== Progress Bar ===================== */

const FancyProgressBar = ({ value }) => (
  <Box sx={{ width: "100%" }}>
    <Box
      sx={{
        height: 18,
        borderRadius: 20,
        background: "rgba(0,0,0,0.12)",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          height: "100%",
          width: `${value}%`,
          borderRadius: 20,
          background:
            "linear-gradient(90deg, #2196f3, #64b5f6, #7c4dff, #2196f3)",
          backgroundSize: "300% 100%",
          animation: `
            ${shimmerLTR} 4.5s linear infinite,
            ${glowPulse} 3s ease-in-out infinite
          `,
          transition: "width 0.9s ease-in-out",
          willChange: "width, background-position"
        }}
      />
    </Box>

    <Typography
      variant="body2"
      fontWeight={700}
      sx={{ mt: 1, textAlign: "right" }}
    >
      {value}%
    </Typography>
  </Box>
);

/* ===================== Status Icon ===================== */

const StatusIcon = ({ status }) => {
  if (status === "COMPLETED")
    return (
      <CheckCircleIcon
        sx={{
          fontSize: 64,
          color: "success.main",
          animation: `${bounce} 0.6s ease-out`
        }}
      />
    );

  if (status === "FAILED")
    return <ErrorIcon sx={{ fontSize: 64, color: "error.main" }} />;

  return (
    <AutoGraphIcon
      sx={{
        fontSize: 64,
        color: "primary.main",
        animation: `${glowPulse} 2.5s infinite`
      }}
    />
  );
};

/* ===================== Modal ===================== */

const ModelGenerationProgressModal = ({
  progress = 0,
  message = "Initializing model generation...",
  status = "IN_PROGRESS",
}) => {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (status === "IN_PROGRESS") {
      setOpen(true);
    }

    if (status === "COMPLETED") {
      setOpen(true);

      const timer = setTimeout(() => {
        setOpen(false);
      }, 1500); // 👈 1.5 seconds

      return () => clearTimeout(timer);
    }

    if (status === "FAILED") {
      setOpen(false);
    }
  }, [status]);

  return (
    <Backdrop
      open={open}
      sx={{
        zIndex: (theme) => theme.zIndex.drawer + 100,
        backdropFilter: "blur(6px)",
        backgroundColor: "rgba(0,0,0,0.4)"
      }}
    >
      <Fade in={open}>
        <Paper
          elevation={20}
          sx={{
            p: 4,
            width: 520,
            borderRadius: 4,
            textAlign: "center",
            background:
              "linear-gradient(145deg, rgba(255,255,255,0.96), rgba(245,248,255,0.96))"
          }}
        >
          <Stack spacing={3} alignItems="center">
            <StatusIcon status={status} />

            <Typography variant="h5" fontWeight={700}>
              Surrogate Model Generation
            </Typography>

            <Typography color="text.secondary">
              {message}
            </Typography>

            {status === "IN_PROGRESS" && (
              <>
                <FancyProgressBar value={progress} />
                <CircularProgress size={28} thickness={4} />
              </>
            )}

            {status === "COMPLETED" && (
              <Typography
                color="success.main"
                fontWeight={700}
                variant="h6"
              >
                🎉 Model generated successfully!
              </Typography>
            )}

            {status === "FAILED" && (
              <Typography
                color="error.main"
                fontWeight={700}
                variant="h6"
              >
                ❌ Generation failed. Please retry.
              </Typography>
            )}
          </Stack>
        </Paper>
      </Fade>
    </Backdrop>
  );
};


export default ModelGenerationProgressModal;
